import React from 'react';
import { Link } from 'react-router-dom';
import { Apple, Smartphone, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ROUTES, BASE_URL } from '../seo/routeMetadata';

/**
 * Serves /app, /ios, and /android — three distinct SEO URLs sharing a layout.
 * `route` prop MUST be one of: '/app' | '/ios' | '/android'.
 */
const AppDownloadPage = ({ route = '/app' }) => {
  const meta = ROUTES[route];

  const iosUrl = 'https://apps.apple.com/us/app/amppredict/id6764166197';
  const androidUrl = 'https://play.google.com/store/apps/details?id=com.anjolc.ampredict';

  return (
    <div className="min-h-screen bg-[#050810]">
      <SEO
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        url={`${BASE_URL}${meta.path}`}
        jsonLd={meta.jsonLd}
      />
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <header className="mb-10 pb-8 border-b border-[#1e2a45]">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs text-gray-500">
            <Link to="/" className="hover:text-white">Home</Link>
            <span className="mx-2 text-gray-600">›</span>
            <span className="text-gray-400">{meta.h1}</span>
          </nav>

          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4" data-testid="page-h1">
            {meta.h1}
          </h1>
          <p className="text-lg text-gray-200 leading-relaxed max-w-3xl">{meta.aeoAnswer}</p>
        </header>

        {/* Store CTAs */}
        <div className="grid gap-4 sm:grid-cols-2 mb-12">
          {(route === '/app' || route === '/ios') && (
            <a
              href={iosUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="ios-store-btn"
              className="flex items-center gap-4 rounded-2xl border border-[#1e2a45] bg-[#131b2e] hover:bg-[#1a2440] p-5 transition-colors"
            >
              <Apple className="h-10 w-10 text-white" />
              <div className="flex-1">
                <div className="text-xs text-gray-400">Download on the</div>
                <div className="text-lg font-semibold text-white">App Store</div>
              </div>
              <ExternalLink className="h-5 w-5 text-gray-400" />
            </a>
          )}
          {(route === '/app' || route === '/android') && (
            <a
              href={androidUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="android-store-btn"
              className="flex items-center gap-4 rounded-2xl border border-[#1e2a45] bg-[#131b2e] hover:bg-[#1a2440] p-5 transition-colors"
            >
              <Smartphone className="h-10 w-10 text-[#3B82F6]" />
              <div className="flex-1">
                <div className="text-xs text-gray-400">Get it on</div>
                <div className="text-lg font-semibold text-white">Google Play</div>
              </div>
              <ExternalLink className="h-5 w-5 text-gray-400" />
            </a>
          )}
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {meta.sections.map((s) => (
            <section key={s.h2}>
              <h2 className="text-2xl font-semibold text-white mb-3">{s.h2}</h2>
              <p className="text-gray-300 leading-relaxed">{s.body}</p>
              {s.href && (
                <a
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-1 text-[#3B82F6] hover:underline text-sm"
                >
                  Open link <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </section>
          ))}
        </div>

        {/* Cross-links to sibling pages */}
        {route === '/app' && (
          <div className="mt-16 rounded-2xl border border-[#1e2a45] bg-[#131b2e] p-6">
            <h3 className="text-xl font-semibold text-white mb-4">Platform-specific pages</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/ios" className="text-[#3B82F6] hover:underline">iOS download page →</Link>
              <Link to="/android" className="text-[#3B82F6] hover:underline">Android download page →</Link>
            </div>
          </div>
        )}
        {route !== '/app' && (
          <div className="mt-16 rounded-2xl border border-[#1e2a45] bg-[#131b2e] p-6">
            <Link to="/app" className="text-[#3B82F6] hover:underline">← Back to all download options</Link>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default AppDownloadPage;
